// ============================================================================
// File: CollectionCoveragePanel.tsx — Spec 187 (US1 · FR-014, US4 regression)
//   · reworked by Spec 215 US3: the % is ACTIONABLE coverage (accepted/other-spec/
//     blocked/agent-only gaps leave the denominator, assessor-side); known gaps
//     render in a collapsed secondary view with reasons — never as daily misses.
// Author: Peter Dobler (Dobler Data Solutions)
// Created: 2026-06-07
// Feature: 215-admin-dashboard-remediation
// Constitution: v1.23.0 | Principle XLII (Unified Entity Access — entity-first read)
// ============================================================================
// Self-fetching panel: reads the precomputed coverage scorecard from
// GET /api/dashboards/coverage and renders per-entity achievable coverage.
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { DashboardPanel, Badge, Spinner, DataTable } from '@/components/ui';
import type { DataTableColumn } from '@/components/ui';
import { apiClient } from '../../../services/apiClient';
import { formatEastern, formatEasternDate } from '@/utils/dateFormatter';
import type { CollectionCoverage } from '../../../../../shared/types/dashboard';

type CoverageRow = CollectionCoverage['entities'][number];

type BarColor = 'success' | 'warning' | 'error' | 'inherit';

const barColor = (pct: number | null): BarColor => {
  if (pct == null) return 'inherit';
  if (pct >= 90) return 'success';
  if (pct >= 70) return 'warning';
  return 'error';
};

// Token color for a bar/text accent (matches the legacy MUI palette intent).
const barColorVar: Record<BarColor, string> = {
  success: 'var(--color-status-success)',
  warning: 'var(--color-status-warning)',
  error: 'var(--color-status-danger)',
  inherit: 'var(--color-text-secondary)',
};

const fmtPct = (pct: number | null): string => (pct == null ? '—' : `${pct.toFixed(1)}%`);
const fmtDate = (iso: string | null): string =>
  iso == null ? '—' : formatEasternDate(iso);

export const CollectionCoveragePanel: React.FC = () => {
  const { data, isLoading, isError } = useQuery<CollectionCoverage>({
    queryKey: ['dashboards', 'coverage'],
    queryFn: async () => {
      const res = await apiClient.get<CollectionCoverage>('/api/dashboards/coverage');
      return res.data;
    },
    staleTime: 60_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
  });

  const loading = isLoading || isError;
  const overall = data?.overallCoveragePct ?? null;
  const prev = data?.previousOverallPct ?? null;
  const regressed = overall != null && prev != null && prev - overall > 0;
  const entities = data?.entities ?? [];
  // Spec 215 US3: known/accepted gaps — collapsed secondary view, never daily misses.
  const acceptedGaps = data?.acceptedGaps ?? [];
  const [gapsOpen, setGapsOpen] = useState(false);

  const columns: DataTableColumn<CoverageRow>[] = [
    { key: 'entityName', header: 'Entity', width: '32%', cellClassName: 'font-semibold' },
    {
      key: 'coverage',
      header: 'Coverage',
      sortValue: (e) => e.achievableCoveragePct ?? -1,
      render: (e) => (
        <div className="flex items-center gap-2">
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-[var(--color-bg-subtle)]">
            <div
              className="h-full"
              style={{
                width: `${Math.max(0, Math.min(100, e.achievableCoveragePct ?? 0))}%`,
                backgroundColor: barColorVar[barColor(e.achievableCoveragePct)],
              }}
            />
          </div>
          <span className="min-w-[44px] text-right text-xs tabular-nums text-[var(--color-text-secondary)]">
            {fmtPct(e.achievableCoveragePct)}
          </span>
        </div>
      ),
    },
    {
      key: 'popAvail',
      header: 'Pop / Avail',
      align: 'right',
      width: '22%',
      sortValue: (e) => e.populatedCount,
      render: (e) => (
        <span className="text-xs tabular-nums text-[var(--color-text-secondary)]">
          {e.populatedCount} / {e.availableCount}
          {e.achievableCount !== e.availableCount && (
            <span title="actionable (excludes accepted/other-spec/blocked/agent-only known gaps)">{' '}(✓{e.achievableCount})</span>
          )}
          {e.blockedByPermission > 0 && (
            <span title="blocked by permission">{' '}(+{e.blockedByPermission}🔒)</span>
          )}
        </span>
      ),
    },
  ];

  return (
    <DashboardPanel
      title="Collection Coverage"
      titleInfo="For each entity type, how many of the collectable columns/fields are actually populated (populated ÷ achievable). Known gaps that are accepted or owned by another spec are excluded from the actionable %."
      actions={
        !loading && overall != null ? (
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold" style={{ color: barColorVar[barColor(overall)] }}>
              {fmtPct(overall)}
            </span>
            {regressed && (
              <Badge
                variant="warning"
                isOutlined
                size="sm"
                title={`Down from ${fmtPct(prev)} at the previous assessment (regression)`}
              >
                ▼ {(prev! - overall!).toFixed(1)}%
              </Badge>
            )}
            {data?.lastAssessedAt && (
              <span
                className="text-xs text-[var(--color-text-secondary)]"
                title={`Last assessed: ${formatEastern(data.lastAssessedAt)}`}
              >
                Assessed {fmtDate(data.lastAssessedAt)}
              </span>
            )}
          </div>
        ) : undefined
      }
    >
      {loading ? (
        <div className="flex min-h-[120px] items-center justify-center">
          <Spinner size="md" />
        </div>
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={entities}
            rowKey={(e) => e.entityName}
            dense
            emptyMessage="No coverage data — run the daily tier (collection_coverage)"
          />
          {/* Spec 215 US3: collapsed known-gaps view — reasons for everything the
              actionable %% deliberately excludes. */}
          {acceptedGaps.length > 0 && (
            <div className="mt-2 border-t border-[var(--color-border)] pt-2">
              <button
                type="button"
                data-testid="accepted-gaps-toggle"
                onClick={() => setGapsOpen((v) => !v)}
                className="flex items-center gap-1 text-xs font-medium text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
              >
                {gapsOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                Known gaps ({acceptedGaps.length}) — excluded from the actionable %
              </button>
              {gapsOpen && (
                <ul data-testid="accepted-gaps-list" className="mt-1.5 space-y-1">
                  {acceptedGaps.map((g) => (
                    <li key={`${g.entity}.${g.column}`} className="flex items-baseline justify-between gap-2 rounded-[var(--radius-md)] bg-[var(--color-bg-subtle)] px-2 py-1">
                      <span className="text-xs font-medium text-[var(--color-text-primary)]">
                        {g.entity}.{g.column}
                      </span>
                      <span className="text-right text-[11px] text-[var(--color-text-secondary)]">
                        {g.reason}
                        <Badge variant="default" size="sm" isOutlined className="ml-1.5">{g.status}</Badge>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}
    </DashboardPanel>
  );
};

export default CollectionCoveragePanel;
